/**
 * The pattern layer: phrases the engine can recognise, and the answers to them.
 *
 * ## Why this exists
 *
 * The owner's brief, in his words: "I want to make sure we think carefully about the
 * direction of harmony, so that we are on a considered journey not just passing from
 * moment to moment." `MOVE_LIBRARY` answers "where could this chord go?" eighty
 * ways and answers it the same way whatever came before — which is the definition of
 * passing from moment to moment. This file answers a different question: *what did the
 * last two or three chords set up, and what would finish it?*
 *
 * ## The gap it fills, precisely
 *
 * The library has no arrivals. Nothing in it is `from: 'key', semitones: 0`, so the only
 * route from G7 to C is `fourth-above-major` — movement kind `functional-subdominant`,
 * whose own prose says it "steps out rather than resolving". The right chord under a
 * false description. The gap was never reachability; it was **prose**, which is why a
 * continuation here is a whole `HeuristicMove` with its own phrase bank rather than a
 * boost applied to something that was already there.
 *
 * They are not in `MOVE_LIBRARY` for two reasons, and the second is the real one:
 * `MOVE_LIBRARY_SIZE` is asserted between sixty and eighty and stands at eighty, so
 * there is no slot left — and a landing must be **unreachable except through a matched
 * pattern**. "The landing this was pointing at" must never appear on a card when nothing
 * was pointing. Nesting the moves inside the patterns that own them makes that structural
 * rather than a convention somebody has to keep.
 *
 * ## Two rules this table keeps
 *
 * 1. **Describe the chords, never the player.** "the landing it points at", not "the ii–V
 *    you set up". A doodling app does not tell you what your plan was, and it is not in a
 *    position to know. `PATTERN_BANNED_WORDS` and `PATTERN_BANNED_PATTERNS` are the lint,
 *    and they apply to this table only: `back to` and `instead` are legitimate in
 *    `MOVE_LIBRARY`, and "wrong" would be a fair word about a chord that never claimed to
 *    have read a phrase.
 * 2. **Continuations are shared by reference.** `TONIC_ARRIVED` serves three patterns as
 *    one object, not three copies that drift apart. A test counts distinct objects
 *    against distinct ids.
 *
 * Everything else about a continuation is an ordinary move: bands, `worksFrom`,
 * `needsKeyMode`, roman numerals, `interpretive`. `build.ts`, `explain.ts` and `toBranch`
 * do not know this file exists.
 */

import type { CurrentQuality, HarmonicPattern, HeuristicMove, QualityMatch } from './types.js';

/**
 * How far back the matcher and the arc read.
 *
 * Four, which is the longest pattern plus one. Longer buys nothing — the longest phrase
 * in the table is three chords — and it costs the one thing a contiguity rule is for:
 * a claim about a tail nobody can still see in the history rail is a claim nobody can
 * check.
 */
export const PATTERN_TAIL_LIMIT = 4;

/**
 * A pattern needs a journey behind it.
 *
 * One chord of history, minimum, whatever the pattern's own length. This is what makes
 * the one-step `standing-dominant` row legitimate, and it is what makes the quiescence
 * invariant absolute: with no path there are no matches, so no rng is drawn and the
 * engine's output is byte-for-byte what it was before this layer existed.
 */
export const PATTERN_MIN_PATH = 1;

/**
 * How settled the key has to be before a key-anchored pattern will name a phrase.
 *
 * `CERTAIN_KEY_CONFIDENCE`, which is stricter than the 0.35 a single move needs, and
 * deliberately so: a move says a chord would sound a certain way, a pattern says *you are
 * in the middle of a ii–V*. Naming a player's own phrase back to them wrongly is the
 * worst failure available here, and the key estimator trails a modulation by two to four
 * chords. Root-anchored patterns need no key at all, which matters — the estimator is at
 * its weakest in the first four chords, exactly when a player is establishing the thing a
 * pattern would recognise.
 */
export const PATTERN_KEY_CONFIDENCE = 0.5;

/** Which readings of a chord each `QualityMatch` accepts. */
export const QUALITY_MATCHES: Readonly<Record<QualityMatch, readonly CurrentQuality[]>> =
  Object.freeze({
    major: Object.freeze(['major'] as const),
    minor: Object.freeze(['minor'] as const),
    dominant: Object.freeze(['dominant'] as const),
    diminished: Object.freeze(['diminished'] as const),
    augmented: Object.freeze(['augmented'] as const),
    suspended: Object.freeze(['suspended'] as const),
    // A V may be a triad or a seventh; a ii may be a minor triad or a half-diminished
    // one. Both are the same step of the same phrase, so they are one match each.
    majorish: Object.freeze(['major', 'dominant'] as const),
    minorish: Object.freeze(['minor', 'diminished'] as const),
  });

/**
 * Words this table does not use, on top of `BANNED_WORDS`.
 *
 * Every one of them describes a player's intention rather than a sound. The engine reads
 * pitch classes; it does not know what anybody meant, and a card that says a chord was
 * "wrong" has claimed to.
 */
export const PATTERN_BANNED_WORDS: readonly string[] = Object.freeze([
  'should',
  'wrong',
  'mistake',
  'correct',
  'supposed',
]);

/**
 * And the phrasings that do the same thing in more than one word.
 *
 * "under your hands" and "where you already are" stay legal: they are about the
 * instrument, not about a choice.
 */
export const PATTERN_BANNED_PATTERNS: readonly RegExp[] = Object.freeze([
  /\byou (set|chose|played|picked|wanted|meant)\b/iu,
  /\byour (choice|plan|phrase|progression|expectation)\b/iu,
]);

// ---------------------------------------------------------------------------
// The continuations.
//
// Fifteen moves, declared once and referenced by identity from the patterns that want
// them. Ids are namespaced `landing/`, `chain/` and `line/` so that an id found in a
// sketch reopened tomorrow, or in the client's explanation memory, says where it came
// from without a lookup.
// ---------------------------------------------------------------------------

/**
 * The plain landing, reached only from the fifth degree.
 *
 * Its prose is careful to claim **root motion and nothing else**, because the V it is
 * offered from may be a dominant seventh, a plain triad or a sus4 — `two-five-major` and
 * `four-five-major` both accept `majorish` and `suspended` on that step, and Dm–G–C is
 * the pop player's default reading of the phrase. So there is no sentence here about a
 * seventh falling a semitone: a G triad has no seventh and a Gsus4 has no third, and the
 * only voice-leading true of all three is the bass. The one landing that *can* talk about
 * a seventh is `landing/tonic-arrived-backdoor`, which is guarded to a dominant.
 */
const TONIC_ARRIVED: HeuristicMove = {
  id: 'landing/tonic-arrived',
  movementKind: 'functional-dominant',
  from: 'key',
  semitones: 0,
  quality: 'major',
  needsKeyMode: 'major',
  band: [0, 0.45],
  romanNumeral: 'I',
  phrases: {
    relationLabels: [
      'the landing it points at',
      'where the phrase closes',
      'the chord it was aiming at',
    ],
    descriptions: [
      'The chord the last two have been aiming at. The bass falls a fifth and the tension goes out of the air.',
      'Both chords before this one lean here. Taking it closes the phrase rather than continuing it.',
      'Plain, and earned. Everything that was leaning stops leaning.',
    ],
    characters: ['settled', 'plain and earned', 'closed'],
    styleTags: ['cadential', 'classic', 'pop'],
    explanation:
      '{to} is where {patternName} has been pointing, {setupPhrase}. It {sharedPhrase}, and {newPhrase}. The bass falls a fifth onto the tonic of {key}, which is the oldest reason in the language for a phrase to sound finished.',
  },
};

const TONIC_ARRIVED_MAJOR_SEVENTH: HeuristicMove = {
  id: 'landing/tonic-arrived-major-seventh',
  movementKind: 'functional-dominant',
  from: 'key',
  semitones: 0,
  quality: 'major',
  seventh: 'maj7',
  needsKeyMode: 'major',
  band: [0.25, 0.8],
  romanNumeral: 'Imaj7',
  phrases: {
    relationLabels: ['the landing, softened', 'home with a 7th on it', 'the arrival, unhurried'],
    descriptions: [
      'The chord the phrase was aiming at, with a major seventh on top. It arrives without quite shutting the door.',
      'Home, but not a full stop. The seventh keeps one note leaning while everything else settles.',
      'The same landing with a softer floor under it. Restful rather than final.',
    ],
    characters: ['restful', 'open and settled', 'unhurried'],
    styleTags: ['jazz', 'lyrical', 'cadential'],
    explanation:
      '{to} is the landing {patternName} set up, {setupPhrase}, with a major seventh added to it. It {sharedPhrase}, and {newPhrase}. The seventh is the one note still leaning, so the phrase comes to rest without closing.',
  },
};

const TONIC_ARRIVED_MINOR: HeuristicMove = {
  id: 'landing/tonic-arrived-minor',
  movementKind: 'functional-dominant',
  from: 'key',
  semitones: 0,
  quality: 'minor',
  needsKeyMode: 'minor',
  band: [0, 0.5],
  romanNumeral: 'i',
  phrases: {
    relationLabels: ['the landing it points at', 'home, minor', 'where the phrase closes'],
    descriptions: [
      'The chord the last two have been aiming at. The leading note steps up and the tension goes.',
      'A minor landing, and none the weaker for it. The phrase closes exactly where it was heading.',
      'Plain and dark. Everything that was pulling stops pulling.',
    ],
    characters: ['settled', 'dark and settled', 'closed'],
    styleTags: ['cadential', 'classical', 'minor'],
    explanation:
      '{to} is where {patternName} has been pointing, {setupPhrase}. It {sharedPhrase}, and {newPhrase}. The third of the chord under your hands steps up a semitone into this root, which is what makes a minor landing sound as final as a major one.',
  },
};

const TONIC_ARRIVED_PICARDY: HeuristicMove = {
  id: 'landing/tonic-arrived-picardy',
  movementKind: 'stylistic-idiom',
  from: 'key',
  semitones: 0,
  quality: 'major',
  needsKeyMode: 'minor',
  band: [0.4, 0.9],
  romanNumeral: 'I',
  phrases: {
    relationLabels: ['the landing, turned major', 'the picardy third', 'home, brightened'],
    descriptions: [
      'The landing the phrase set up, with a major third where the minor one was. The room lights up on the last chord.',
      'Everything arrives where it was heading and the third turns the other way. Old, and still surprising.',
      'A minor phrase closing on a major chord. Nothing else changes; one note does all of it.',
    ],
    characters: ['bright', 'lit from inside', 'lifted'],
    styleTags: ['classical', 'baroque', 'lyrical'],
    explanation:
      '{to} is the landing {patternName} was pointing at, {setupPhrase}, with its third raised. It {sharedPhrase}, and {newPhrase}. The raised third is the whole of the effect, and it works because everything else about the arrival is exactly as prepared.',
  },
};

/**
 * The backdoor's own landing, and the reason it is not `TONIC_ARRIVED` with a different
 * name.
 *
 * ♭VII7 → I approaches the tonic from a whole tone **below**, so every sentence the
 * ordinary cadence says about it is false: the bass rises rather than falling a fifth,
 * and the dominant's seventh lands on the tonic's fifth rather than its third — a
 * dominant on R has its seventh at R+10, the backdoor target is R+2, and R+10 is four
 * semitones from that chord's third and one from its fifth. B♭7 into C shares no note
 * with it at all, which `{sharedPhrase}` says out loud one sentence earlier, so a landing
 * claiming a note "falls into" a chord tone of it would contradict its own paragraph.
 *
 * `worksFrom: ['dominant']` is what earns the voice-leading sentence: this is the one
 * landing in the table that knows there is a seventh to talk about.
 */
const TONIC_ARRIVED_BACKDOOR: HeuristicMove = {
  id: 'landing/tonic-arrived-backdoor',
  movementKind: 'functional-dominant',
  from: 'key',
  semitones: 0,
  quality: 'major',
  worksFrom: ['dominant'],
  needsKeyMode: 'major',
  band: [0, 0.45],
  romanNumeral: 'I',
  phrases: {
    relationLabels: [
      'the landing it points at',
      'in through the back door',
      'where the phrase closes',
    ],
    descriptions: [
      'The chord the last two have been leaning towards, arriving from a whole tone below. Warm, and just as final.',
      'A cadence that comes in from underneath. The bass rises a tone instead of falling a fifth, and the phrase still closes.',
      'Everything lands, and none of it arrives from where the ear was watching.',
    ],
    characters: ['warm', 'settled', 'quietly final'],
    styleTags: ['jazz', 'soul', 'cadential'],
    explanation:
      '{to} is where {patternName} has been pointing, {setupPhrase}. It {sharedPhrase}, and {newPhrase}. The seventh of the chord under your hands falls a semitone onto the fifth of this one while the root rises a whole tone into it, which is why an arrival from below closes a phrase as firmly as one from above.',
  },
};

const TONIC_EVADED: HeuristicMove = {
  id: 'landing/tonic-evaded',
  movementKind: 'deceptive',
  from: 'key',
  semitones: 9,
  quality: 'minor',
  needsKeyMode: 'major',
  band: [0.3, 0.85],
  romanNumeral: 'vi',
  phrases: {
    relationLabels: ['the landing, moved', 'the sixth instead', 'a step to the side'],
    descriptions: [
      // The submediant sits as far below the tonic as the mediant sits above it: nine
      // semitones up is a minor third *down*, which is what `motionPhrase(9)` says and
      // what `relative-minor` calls the same interval.
      'The same preparation, arriving a third below where it was aimed. The phrase carries on rather than stopping.',
      'Two of the three expected notes are here. The one that is missing is the reason this does not feel like an ending.',
      'The tension goes and the sentence does not close. Somewhere between an arrival and a swerve.',
    ],
    characters: ['sidelong', 'soft and open', 'continuing'],
    styleTags: ['pop', 'classical', 'lyrical'],
    explanation:
      '{to} takes the place {patternName} prepared, {setupPhrase}, a third below the chord it named. It {sharedPhrase}, and {newPhrase}. It keeps two of the notes the ear was waiting for, which is why it is accepted, and loses the one that would have closed the phrase.',
  },
};

const TONIC_SIDESTEPPED: HeuristicMove = {
  id: 'landing/tonic-sidestepped',
  movementKind: 'stylistic-idiom',
  from: 'key',
  semitones: 1,
  quality: 'major',
  seventh: 'maj7',
  band: [0.55, 1],
  romanNumeral: '♭IImaj7',
  // A tritone-substitute landing genuinely reads two ways, so it never claims certainty
  // however settled the key is. The plain landings above are not readings and are not
  // marked: a ii–V–I in a settled key is a fact, and demoting it would understate what
  // the engine actually knows.
  interpretive: true,
  phrases: {
    relationLabels: ['the landing, sidestepped', 'a semitone off home', 'the arrival, displaced'],
    descriptions: [
      'The preparation pays out a semitone above where it was pointing. The tension goes; the destination moves.',
      'A landing that lands somewhere else. Nothing is left hanging and nothing arrives where it was aimed.',
      'It releases on time and in another room. Wide and lit, with the old key still audible under it.',
    ],
    characters: ['displaced', 'wide and lit', 'released'],
    styleTags: ['jazz', 'modern', 'film'],
    explanation:
      '{to} sits a semitone above the chord {patternName} was pointing at, {setupPhrase}. It {sharedPhrase}, and {newPhrase}. The pull discharges on time and into a chord the key does not own, so the phrase relaxes and moves house at once.',
  },
};

/**
 * The three landings below are `standing-dominant`'s, and all three are gated on the
 * target belonging to the key.
 *
 * The pattern itself consults no key on purpose — *you are standing on a dominant
 * seventh* is a fact about the sonority, and the estimator is at its weakest in the first
 * four chords. But the continuations say something much stronger, in prose that is
 * emphatic: "what it resolves to", "the most ordinary thing in tonal music". That is true
 * of every dominant on a degree the key owns — G7 is V, D7 is V/V, A7 is V/ii, C7 is I7
 * heading for IV — and false of every dominant that is a substitute for one: A♭7 in a
 * settled C major is the tritone sub for G7 and does not go to D♭, B♭7 is the backdoor
 * and goes up a tone, F7 is the blues IV7 and goes home rather than to B♭.
 *
 * Testing the **target** rather than the current chord's degree is what picks the right
 * one of the three as well: standing on A7 in C major, D major is outside the key and D
 * minor is not, so the card is Dm — which is where V/ii goes. Below
 * `KEY_SETTLED_CONFIDENCE` the gate does not apply at all and the fifth below is offered
 * as the sonority's own default, which is the whole reason this pattern needs no key.
 */
const DOMINANT_LANDS_MAJOR: HeuristicMove = {
  id: 'landing/dominant-lands-major',
  movementKind: 'functional-dominant',
  from: 'root',
  semitones: 5,
  quality: 'major',
  worksFrom: ['dominant'],
  needsDiatonicTarget: true,
  band: [0, 0.5],
  phrases: {
    relationLabels: ['what it resolves to', 'a fourth up, resolved', 'the chord it wants'],
    descriptions: [
      'The chord the seventh under your hands is leaning into. The tritone inside it opens outward and stops.',
      'A fifth down, and the pull is gone. The most ordinary thing in tonal music, and it still works.',
      'Where the tension goes. Two notes move by a semitone and the chord stops asking for anything.',
    ],
    characters: ['settled', 'plain and earned', 'at rest'],
    styleTags: ['classic', 'cadential', 'pop'],
    explanation:
      '{to} is what the dominant under your hands resolves to, {setupPhrase}. It {sharedPhrase}, and {newPhrase}. The seventh falls a semitone and the third rises one, which is the oldest reason in the language for a chord to sound finished.',
  },
};

/**
 * The minor twin, and the arithmetic its prose used to get backwards.
 *
 * A dominant on R has its seventh at R+10; the minor target on R+5 is
 * {R+5, R+8, R+0}. R+10 falling a **whole tone** reaches R+8, which is that chord's
 * **third** — not a semitone, and not its fifth. The leading note at R+4 rises a semitone
 * into the root at R+5. Which is exactly what `landing/tonic-arrived-minor` already says
 * correctly, and what the major twin's "the seventh falls a semitone and the third rises
 * one" says correctly for its own target.
 *
 * `needsKeyMode: 'minor'` is deliberately **not** set. A V7 landing minor is not a fact
 * about the key's mode, it is a fact about which chord the key owns: A7 in a settled C
 * *major* wants D minor, and gating on the mode meant offering D major there. The
 * diatonic-target gate says the same thing better, and in both modes.
 */
const DOMINANT_LANDS_MINOR: HeuristicMove = {
  id: 'landing/dominant-lands-minor',
  movementKind: 'functional-dominant',
  from: 'root',
  semitones: 5,
  quality: 'minor',
  worksFrom: ['dominant'],
  needsDiatonicTarget: true,
  band: [0, 0.55],
  phrases: {
    relationLabels: ['what it resolves to', 'a fourth up, minor', 'the minor landing'],
    descriptions: [
      'The chord the seventh is leaning into, and it lands minor. The pull is the same; the room is darker.',
      'A fifth down into the minor. Everything resolves and nothing brightens.',
      'Where the tension goes when the chord above it is minor. Plain, dark, and finished.',
    ],
    characters: ['dark and settled', 'grave', 'at rest'],
    styleTags: ['classical', 'cadential', 'minor'],
    explanation:
      '{to} is what the dominant under your hands resolves to, {setupPhrase}. It {sharedPhrase}, and {newPhrase}. The seventh falls a whole tone into the third of this chord and the leading note rises a semitone into its root, which is what makes a minor landing as final as a major one.',
  },
};

const DOMINANT_LANDS_MAJOR_SEVENTH: HeuristicMove = {
  id: 'landing/dominant-lands-major-seventh',
  movementKind: 'functional-dominant',
  from: 'root',
  semitones: 5,
  quality: 'major',
  seventh: 'maj7',
  worksFrom: ['dominant'],
  needsDiatonicTarget: true,
  band: [0.3, 0.85],
  phrases: {
    relationLabels: ['the resolution, softened', 'a fourth up, with a 7th', 'resolved, not closed'],
    descriptions: [
      'The chord the seventh is leaning into, with a major seventh of its own. It resolves and keeps the door open.',
      'The pull discharges into something restful rather than final. Good when the phrase is not finished.',
      'A landing with one note still leaning. Settled, but not a full stop.',
    ],
    characters: ['restful', 'open and settled', 'unhurried'],
    styleTags: ['jazz', 'lyrical', 'modern'],
    explanation:
      '{to} is what the dominant under your hands resolves to, {setupPhrase}, with a major seventh on it. It {sharedPhrase}, and {newPhrase}. The old tension is gone and one new leaning note takes its place, so the phrase settles without stopping.',
  },
};

const NEXT_LINK_MAJOR: HeuristicMove = {
  id: 'chain/next-link-major',
  movementKind: 'functional-dominant',
  from: 'root',
  semitones: 5,
  quality: 'major',
  band: [0, 0.5],
  phrases: {
    relationLabels: ['one more link', 'another fifth down', 'the chain continues'],
    descriptions: [
      'The roots have been falling a fifth at a time. This is the next one, and the wheel keeps turning.',
      'A chain of fifths pulls forward on its own. Taking this is agreeing to keep going.',
      'The same drop again. Sequences like this are the easiest motion in music to hear coming.',
    ],
    characters: ['inevitable', 'turning', 'onward'],
    styleTags: ['classic', 'baroque', 'jazz'],
    explanation:
      '{to} is the next link in {patternName}, {setupPhrase} — another root a fifth below the last. It {sharedPhrase}, and {newPhrase}. A chain like this can be broken at any point, and breaking it is the loudest thing available while it is turning.',
  },
};

const NEXT_LINK_MINOR: HeuristicMove = {
  id: 'chain/next-link-minor',
  movementKind: 'functional-dominant',
  from: 'root',
  semitones: 5,
  quality: 'minor',
  worksFrom: ['minor', 'diminished'],
  band: [0.1, 0.6],
  phrases: {
    relationLabels: ['one more link, minor', 'another fifth down', 'the chain continues'],
    descriptions: [
      // No diatonic claim, because this move has no key gate and cannot make one. A
      // diatonic circle of fifths changes quality at ii (vii°–iii–vi–ii–V–I–IV), so
      // minor→minor is diatonic for two of the three minor-rooted links in a major key
      // and leaves the key at the third: Em–Am–Dm–Gm puts a B♭ in C major, which the
      // engine's own numeral (`v`) and confidence (`plausible`) already report.
      'The next root down a fifth, minor like the last. The sequence keeps one colour all the way down.',
      'Another turn of the same wheel, in the same colour. Nothing brightens and nothing stops.',
      'The chain continues without changing character. Motion without event.',
    ],
    characters: ['turning', 'level', 'onward'],
    styleTags: ['classic', 'folk', 'lyrical'],
    explanation:
      '{to} is the next link in {patternName}, {setupPhrase} — a fifth below the chord under your hands and minor like it. It {sharedPhrase}, and {newPhrase}. A chain that keeps one quality is moving by sound rather than by key, so it can run past the edge of the key it started in without the ear objecting.',
  },
};

const NEXT_LINK_DOMINANT: HeuristicMove = {
  id: 'chain/next-link-dominant',
  movementKind: 'secondary-dominant',
  from: 'root',
  semitones: 5,
  quality: 'major',
  seventh: 'min7',
  worksFrom: ['dominant'],
  band: [0.2, 0.7],
  phrases: {
    relationLabels: ['one more link, with a 7th', 'the next dominant', 'the chain, still turning'],
    descriptions: [
      'The next root down a fifth, with a seventh on it. Each chord is the dominant of the one after.',
      'A chain of dominants: nothing resolves, everything points at the next thing.',
      'The wheel keeps turning and every chord is leaning. This can go all the way round the twelve.',
    ],
    characters: ['driving', 'taut', 'onward'],
    styleTags: ['jazz', 'ragtime', 'gypsy'],
    explanation:
      '{to} is the next link in {patternName}, {setupPhrase}, and it carries a seventh of its own. It {sharedPhrase}, and {newPhrase}. Every chord in a chain like this is the dominant of the next, so the pull renews itself instead of discharging.',
  },
};

/**
 * The two line continuations carry the shape across, because the patterns that own them
 * are about a shape.
 *
 * `quality: 'same'` is planing's mechanism, and `build.ts` spells out why planing needs
 * it: "a minor seventh planed up a tone that arrived as a plain triad would make the
 * move's own explanation false — the shape it kept would be a shape the player was not
 * playing." These two make exactly that claim — "one more semitone down", "a falling line
 * is heard as one gesture" — so a hard-coded major triad answering a line of minor triads
 * would be the gesture ending rather than continuing, which is the opposite of what the
 * card says. `sameShape` on the owning patterns is what makes the copy honest: the whole
 * matched tail is one reading, so "the same shape again" is a fact rather than a guess.
 */
const CLIMB_TOPPED: HeuristicMove = {
  id: 'line/climb-topped',
  movementKind: 'stylistic-idiom',
  from: 'root',
  semitones: 1,
  quality: 'same',
  band: [0, 0.55],
  phrases: {
    relationLabels: ['where the climb stops', 'the top of the line', 'one more step, and still'],
    descriptions: [
      'The roots have been rising a semitone at a time. This is the step the line was rising towards.',
      'A climb only means something when it stops. This is where it stops.',
      'One more semitone up, and then nothing. After that much lifting the stillness is the event.',
    ],
    characters: ['topped out', 'high and still', 'arrived'],
    styleTags: ['film', 'pop', 'cinematic'],
    explanation:
      '{to} is one more semitone up, the step {patternName} was climbing towards, {setupPhrase}. It {sharedPhrase}, and {newPhrase}. A rising line is heard as one gesture, so where it stops is heard as where it meant to go.',
  },
};

const DESCENT_BOTTOMED: HeuristicMove = {
  id: 'line/descent-bottomed',
  movementKind: 'stylistic-idiom',
  from: 'root',
  semitones: 11,
  quality: 'same',
  band: [0, 0.55],
  phrases: {
    relationLabels: ['where the fall stops', 'the bottom of the line', 'one more step down'],
    descriptions: [
      'The roots have been falling a semitone at a time. This is the step the line was falling towards.',
      'A descent reads as a sentence and this is the full stop. The floor stops sinking.',
      'One more semitone down, and then stillness. The weight of the fall lands here.',
    ],
    characters: ['settled', 'low and still', 'grounded'],
    styleTags: ['film', 'lyrical', 'cinematic'],
    explanation:
      '{to} is one more semitone down, the step {patternName} was falling towards, {setupPhrase}. It {sharedPhrase}, and {newPhrase}. A falling line is heard as one gesture, so the chord it stops on carries the weight of the whole descent.',
  },
};

// ---------------------------------------------------------------------------
// The patterns.
//
// Ordering within the table is a tie-break only: a longer tail wins first, then a
// key-anchored match over a root-anchored one, then this order. There is no `strength`
// number, because with one pattern slot per set "the most specific match that has an
// eligible continuation" needs no numbers — and a number nobody can derive is a number
// somebody will tune.
//
// Collisions are features. Am–Dm–G7 is both a chain of falling fifths and a ii–V, both
// readings are true, and both land on C. That is the same thing `library.ts` already says
// about A♭ from C being a chromatic mediant and a borrowed ♭VI at once.
// ---------------------------------------------------------------------------

export const PATTERN_LIBRARY: readonly HarmonicPattern[] = Object.freeze<HarmonicPattern[]>([
  {
    id: 'two-five-major',
    anchor: 'key',
    needsKeyMode: 'major',
    steps: [
      { semitones: 2, quality: ['minorish'] },
      { semitones: 7, quality: ['majorish', 'suspended'] },
    ],
    name: 'the ii–V',
    continuations: [TONIC_ARRIVED, TONIC_ARRIVED_MAJOR_SEVENTH, TONIC_EVADED, TONIC_SIDESTEPPED],
    // The other endings to the same sentence are already in the library; a second pattern
    // slot to hold them would have been a funnel, and this is a tilt.
    boostMoveIds: ['key-deceptive-to-the-sixth', 'deceptive-landing-minor'],
  },
  {
    id: 'two-five-minor',
    anchor: 'key',
    needsKeyMode: 'minor',
    steps: [
      { semitones: 2, quality: ['minorish'] },
      { semitones: 7, quality: ['majorish'] },
    ],
    name: 'the minor ii–V',
    continuations: [TONIC_ARRIVED_MINOR, TONIC_ARRIVED_PICARDY, TONIC_SIDESTEPPED],
    boostMoveIds: ['key-deceptive-to-the-sixth-major'],
  },
  {
    id: 'four-five-major',
    anchor: 'key',
    needsKeyMode: 'major',
    steps: [
      { semitones: 5, quality: ['majorish', 'suspended'] },
      { semitones: 7, quality: ['majorish', 'suspended'] },
    ],
    name: 'the IV–V',
    continuations: [TONIC_ARRIVED, TONIC_ARRIVED_MAJOR_SEVENTH, TONIC_EVADED, TONIC_SIDESTEPPED],
    boostMoveIds: ['key-deceptive-to-the-sixth', 'deceptive-landing-minor'],
  },
  {
    id: 'backdoor-open',
    anchor: 'key',
    needsKeyMode: 'major',
    steps: [
      { semitones: 5, quality: ['minorish'] },
      { semitones: 10, quality: ['dominant'] },
    ],
    name: 'the backdoor cadence',
    // Its own plain landing rather than the ii–V's. `TONIC_ARRIVED` describes a bass
    // falling a fifth; this cadence arrives from a whole tone below, so the two phrases
    // cannot share one paragraph however alike the chord on the card looks.
    continuations: [TONIC_ARRIVED_MAJOR_SEVENTH, TONIC_ARRIVED_BACKDOOR],
  },
  {
    id: 'deceptive-landed',
    anchor: 'key',
    needsKeyMode: 'major',
    steps: [
      { semitones: 7, quality: ['majorish'] },
      { semitones: 9, quality: ['minorish'] },
    ],
    name: 'the dodged cadence',
    // Injects nothing on purpose. After a cadence has been dodged the useful offer is not
    // a new chord but a tilt back toward the chords that set the cadence up in the first
    // place, so the phrase can be tried again. A pattern with only boosts is still a
    // pattern; a test requires continuations or boosts, never neither.
    continuations: [],
    boostMoveIds: ['key-supertonic-seventh', 'key-dominant-seventh'],
  },
  {
    id: 'standing-dominant',
    anchor: 'root',
    // The one-step row, and the deliberate exception to "two chords minimum". The claim —
    // *you are standing on a dominant seventh* — is a fact about the sonority under the
    // player's hands rather than a reading of a phrase, needs no key, and cannot
    // over-claim. `PATTERN_MIN_PATH` still keeps it off the first chord of a session.
    steps: [{ semitones: 0, quality: ['dominant'] }],
    name: 'the dominant under your hands',
    continuations: [DOMINANT_LANDS_MAJOR, DOMINANT_LANDS_MINOR, DOMINANT_LANDS_MAJOR_SEVENTH],
    boostMoveIds: ['deceptive-landing-minor'],
  },
  {
    id: 'falling-fifths',
    anchor: 'root',
    steps: [{ semitones: 2 }, { semitones: 7 }, { semitones: 0 }],
    name: 'the chain of falling fifths',
    continuations: [NEXT_LINK_MAJOR, NEXT_LINK_MINOR, NEXT_LINK_DOMINANT],
    // Two substitutes, because one of them is unreachable in this pattern's headline
    // case. `tritone-substitute-dominant` refuses a dominant (`worksFrom` has every
    // reading but that one), and the canonical jazz chain — Am–Dm–G7 — ends on one, so
    // on its own it applies a tilt that can never fire. `tritone-substitute-of-this` is
    // the same idea `worksFrom: ['dominant']`, and between them the chain is tilted
    // toward a substitution wherever it happens to stop.
    boostMoveIds: ['tritone-substitute-dominant', 'tritone-substitute-of-this'],
  },
  {
    id: 'chromatic-ascent',
    anchor: 'root',
    steps: [{ semitones: 10 }, { semitones: 11 }, { semitones: 0 }],
    sameShape: true,
    name: 'the climb',
    continuations: [CLIMB_TOPPED],
    boostMoveIds: ['plane-up-a-semitone', 'plane-up-a-tone'],
  },
  {
    id: 'chromatic-descent',
    anchor: 'root',
    steps: [{ semitones: 2 }, { semitones: 1 }, { semitones: 0 }],
    sameShape: true,
    name: 'the descent',
    continuations: [DESCENT_BOTTOMED],
    boostMoveIds: ['plane-down-a-semitone', 'plane-down-a-tone'],
  },
]);

/** Every pattern, indexed by `id`. */
export const PATTERNS_BY_ID: ReadonlyMap<string, HarmonicPattern> = new Map(
  PATTERN_LIBRARY.map((pattern) => [pattern.id, pattern]),
);

/** How many patterns the table holds. Exported so callers do not count by hand. */
export const PATTERN_LIBRARY_SIZE = PATTERN_LIBRARY.length;
